import { useState, useEffect, useCallback, useMemo } from 'react';
import { useAuth } from '../contexts/AuthContext';
import axios from 'axios';
import { toast } from 'sonner';
import { Card, CardContent } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Button } from '../components/ui/button';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../components/ui/dialog';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle
} from '../components/ui/alert-dialog';
import { Checkbox } from '../components/ui/checkbox';
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator, DropdownMenuTrigger
} from '../components/ui/dropdown-menu';
import { UserCog, Building2, Plus, Eye, EyeOff, KeyRound, MoreVertical, Pencil, Power, Trash2, ShieldCheck, Users, ClipboardList, PenLine, Loader2, Search, X } from 'lucide-react';
import { MODULE_OPTIONS, ROLE_DEFAULT_MODULES } from '../config/modules';

const ROLE_OPTIONS = [
  { id: 'admin',                label: 'Admin',                desc: 'Org-wide operational control · all modules · approvals' },
  { id: 'area_manager',         label: 'Area Manager',         desc: 'Admin-like access across multiple outlets' },
  { id: 'manager',              label: 'Manager',              desc: 'Outlet ops owner — leave modules empty for full access' },
  { id: 'supervisor',           label: 'Supervisor',           desc: 'View & edit only assigned modules — visibility is strictly assignment-driven' },
  { id: 'production_head',      label: 'Production Head',      desc: 'Custom role — access driven entirely by selected modules' },
  { id: 'maintenance_manager',  label: 'Maintenance Manager',  desc: 'Scoped admin — sees ONLY assigned modules (default: Maintenance)' },
  { id: 'loyalty_sales',        label: 'Loyalty Sales',        desc: 'View Loyalty and sell approved memberships/vouchers across assigned outlets' },
  { id: 'chef',                 label: 'Chef',                 desc: 'Kitchen execution — recipes & production by default' },
  { id: 'accountant',           label: 'Accountant',           desc: 'P&L, vouchers, financial reports' },
  { id: 'store_manager',        label: 'Store Manager',        desc: 'Inventory, store issues, requisitions' },
  { id: 'cashier',              label: 'Cashier',              desc: 'Daily entry & sales capture' },
  { id: 'maintenance',          label: 'Maintenance',          desc: 'Maintenance / energy / preventive ops' },
];

// ROLE_DEFAULT_MODULES is now imported from /config/modules — single source of truth.

const ROLE_COLORS = {
  admin: 'bg-[color:var(--color-primary)]/10 text-[color:var(--color-primary)] border-[color:var(--color-primary)]/30',
  area_manager: 'bg-purple-50 text-purple-700 border-purple-200',
  manager: 'bg-[#2D6A4F]/10 text-[#2D6A4F] border-[#2D6A4F]/20',
  supervisor: 'bg-blue-50 text-blue-700 border-blue-200',
  production_head: 'bg-amber-50 text-amber-700 border-amber-200',
  maintenance_manager: 'bg-yellow-50 text-yellow-800 border-yellow-200',
  loyalty_sales: 'bg-amber-50 text-amber-800 border-amber-200',
  chef: 'bg-orange-50 text-orange-700 border-orange-200',
  accountant: 'bg-emerald-50 text-emerald-700 border-emerald-200',
  store_manager: 'bg-slate-50 text-slate-700 border-slate-200',
  cashier: 'bg-pink-50 text-pink-700 border-pink-200',
  maintenance: 'bg-yellow-50 text-yellow-800 border-yellow-200',
};

export default function AdminManagers() {
  const { getHeaders, API } = useAuth();
  const [managers, setManagers] = useState([]);
  const [outlets, setOutlets] = useState([]);
  const [loading, setLoading] = useState(true);
  // Search — derived list so we don't refetch on every keystroke.
  const [searchQuery, setSearchQuery] = useState('');

  // Assign outlets dialog
  const [assignOpen, setAssignOpen] = useState(false);
  const [assignMgr, setAssignMgr] = useState(null);
  const [assignIds, setAssignIds] = useState([]);
  const [assignSaving, setAssignSaving] = useState(false);

  // Create dialog
  const [createOpen, setCreateOpen] = useState(false);
  const [createStep, setCreateStep] = useState('role');
  const [createForm, setCreateForm] = useState({ name: '', email: '', designation: '', phone: '', password: '', outlet_ids: [], role: 'manager', assigned_modules: [], pin: '' });
  const [creating, setCreating] = useState(false);
  const [showPassword, setShowPassword] = useState(false);

  // Module assignment dialog
  const [moduleOpen, setModuleOpen] = useState(false);
  const [moduleMgr, setModuleMgr] = useState(null);
  const [moduleIds, setModuleIds] = useState([]);
  const [moduleSaving, setModuleSaving] = useState(false);

  // PIN dialog
  const [pinOpen, setPinOpen] = useState(false);
  const [pinMgr, setPinMgr] = useState(null);
  const [pinValue, setPinValue] = useState('');
  const [pinSaving, setPinSaving] = useState(false);

  // Reset password dialog
  const [resetOpen, setResetOpen] = useState(false);
  const [resetMgr, setResetMgr] = useState(null);
  const [newPassword, setNewPassword] = useState('');
  const [showNewPw, setShowNewPw] = useState(false);
  const [resetting, setResetting] = useState(false);

  // Signature management dialog (approvers only — admin / area_manager)
  const [sigOpen, setSigOpen] = useState(false);
  const [sigMgr, setSigMgr] = useState(null);
  const [sigSaving, setSigSaving] = useState(false);

  const openSig = (mgr) => { setSigMgr(mgr); setSigOpen(true); };

  const handleSignatureUpload = async (e) => {
    const f = e.target.files?.[0];
    if (!f || !sigMgr) return;
    if (f.size > 1_200_000) { toast.error('Image is too large — keep under 1.2 MB'); return; }
    setSigSaving(true);
    try {
      const reader = new FileReader();
      reader.onload = async () => {
        try {
          await axios.put(`${API}/admin/managers/${sigMgr.id}/signature`,
            { signature_b64: reader.result }, { headers: getHeaders() });
          toast.success(`Signature saved for ${sigMgr.name}`);
          setSigOpen(false);
          load();
        } catch (err) {
          toast.error(err.response?.data?.detail || 'Could not save signature');
        } finally {
          setSigSaving(false);
        }
      };
      reader.readAsDataURL(f);
    } catch {
      toast.error('Could not read image');
      setSigSaving(false);
    }
  };

  // Edit manager dialog
  const [editOpen, setEditOpen] = useState(false);
  const [editMgr, setEditMgr] = useState(null);
  const [editForm, setEditForm] = useState({ name: '', email: '', designation: '', phone: '', role: 'manager', assigned_modules: [] });
  const [editSaving, setEditSaving] = useState(false);

  // Delete confirmation
  const [deleteConfirm, setDeleteConfirm] = useState(null);
  const [statusFilter, setStatusFilter] = useState('active');

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [mgrRes, outRes] = await Promise.all([
        axios.get(`${API}/admin/managers`, { headers: getHeaders(), params: { status_filter: statusFilter } }),
        axios.get(`${API}/admin/outlets`, { headers: getHeaders() })
      ]);
      setManagers(mgrRes.data);
      setOutlets(outRes.data.filter(o => o.status !== 'inactive'));
    } catch {
      toast.error('Failed to load data');
    } finally {
      setLoading(false);
    }
  }, [API, getHeaders, statusFilter]);

  useEffect(() => { load(); }, [load]);

  // Derived: search-filtered managers. Matches name, email, phone, designation, role
  // (all case-insensitive). Empty query returns the full list.
  const filteredManagers = useMemo(() => {
    const q = (searchQuery || '').trim().toLowerCase();
    if (!q) return managers;
    return managers.filter(m => {
      const haystack = [
        m.name, m.email, m.phone, m.designation, m.role,
      ].filter(Boolean).join(' ').toLowerCase();
      return haystack.includes(q);
    });
  }, [managers, searchQuery]);

  // === Assign Outlets ===
  const openAssign = (mgr) => {
    setAssignMgr(mgr);
    setAssignIds([...(mgr.assigned_outlet_ids || [])]);
    setAssignOpen(true);
  };

  const toggleAssignId = (id) => {
    setAssignIds(prev => prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]);
  };

  const handleAssign = async () => {
    if (assignIds.length === 0) { toast.error('At least one outlet must be assigned'); return; }
    setAssignSaving(true);
    try {
      await axios.put(
        `${API}/admin/managers/${assignMgr.id}/assign-outlets`,
        { outlet_ids: assignIds },
        { headers: getHeaders() }
      );
      toast.success('Outlets updated');
      setAssignOpen(false);
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to assign outlets');
    } finally { setAssignSaving(false); }
  };

  // === Create Manager ===
  const openCreate = () => {
    setCreateForm({ name: '', email: '', designation: '', phone: '', password: '', outlet_ids: [], role: 'manager', assigned_modules: [], pin: '' });
    setCreateStep('role');
    setShowPassword(false);
    setCreateOpen(true);
  };

  const toggleCreateOutlet = (id) => {
    setCreateForm(p => ({
      ...p,
      outlet_ids: p.outlet_ids.includes(id) ? p.outlet_ids.filter(x => x !== id) : [...p.outlet_ids, id]
    }));
  };

  const handleCreate = async () => {
    if (!createForm.name.trim()) { toast.error('Name is required'); return; }
    if (!createForm.email.trim()) { toast.error('Email is required'); return; }
    if (createForm.password.length < 6) { toast.error('Password must be at least 6 characters'); return; }
    if (createForm.outlet_ids.length === 0) { toast.error('At least one outlet must be selected'); return; }
    if ((createForm.role === 'supervisor' || createForm.role === 'production_head' || createForm.role === 'maintenance_manager') && createForm.assigned_modules.length === 0) { toast.error(`Assign at least one module for ${createForm.role === 'supervisor' ? 'supervisor' : 'production head'}`); return; }
    setCreating(true);
    try {
      const res = await axios.post(`${API}/admin/managers`, createForm, { headers: getHeaders() });
      const roleLabel = ROLE_OPTIONS.find(r => r.id === createForm.role)?.label || createForm.role;
      toast.success(`${roleLabel} ${res.data.name} created`);
      setCreateOpen(false);
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to create user');
    } finally { setCreating(false); }
  };

  // === Reset Password ===
  const openReset = (mgr) => {
    setResetMgr(mgr);
    setNewPassword('');
    setShowNewPw(false);
    setResetOpen(true);
  };

  const handleReset = async () => {
    if (newPassword.length < 6) { toast.error('Password must be at least 6 characters'); return; }
    setResetting(true);
    try {
      await axios.post(
        `${API}/admin/managers/${resetMgr.id}/reset-password`,
        { new_password: newPassword },
        { headers: getHeaders() }
      );
      toast.success(`Password reset for ${resetMgr.name}`);
      setResetOpen(false);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to reset password');
    } finally { setResetting(false); }
  };

  // === Edit Manager ===
  const openEdit = (mgr) => {
    setEditMgr(mgr);
    setEditForm({ name: mgr.name, email: mgr.email, designation: mgr.designation || '', phone: mgr.phone || '', role: mgr.role || 'manager', assigned_modules: mgr.assigned_modules || [] });
    setEditOpen(true);
  };

  const handleEdit = async () => {
    if (!editForm.name.trim()) { toast.error('Name is required'); return; }
    if ((editForm.role === 'supervisor' || editForm.role === 'production_head' || editForm.role === 'maintenance_manager') && editForm.assigned_modules.length === 0) { toast.error('Assign at least one module'); return; }
    setEditSaving(true);
    try {
      await axios.put(`${API}/admin/managers/${editMgr.id}`, editForm, { headers: getHeaders() });
      toast.success('User updated');
      setEditOpen(false);
      load();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed'); }
    finally { setEditSaving(false); }
  };

  // === Toggle Status ===
  const handleToggleStatus = async (mgr) => {
    const newStatus = mgr.status === 'active' ? 'inactive' : 'active';
    try {
      await axios.put(`${API}/admin/managers/${mgr.id}/status`, { status: newStatus }, { headers: getHeaders() });
      toast.success(`${mgr.name} ${newStatus === 'active' ? 'activated' : 'deactivated'}`);
      load();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed'); }
  };

  // === Archive (soft-delete) ===
  const handleDelete = async () => {
    if (!deleteConfirm) return;
    try {
      await axios.delete(`${API}/admin/managers/${deleteConfirm.id}`, { headers: getHeaders() });
      toast.success(`${deleteConfirm.name} archived`);
      setDeleteConfirm(null);
      load();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed'); }
  };

  // === Restore (un-archive / re-activate) ===
  const handleRestore = async (mgr) => {
    try {
      await axios.post(`${API}/admin/managers/${mgr.id}/restore`, {}, { headers: getHeaders() });
      toast.success(`${mgr.name} restored`);
      load();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed'); }
  };

  // === Module Assignment ===
  const openModules = (mgr) => {
    setModuleMgr(mgr);
    setModuleIds([...(mgr.assigned_modules || [])]);
    setModuleOpen(true);
  };

  const handleModuleSave = async () => {
    setModuleSaving(true);
    try {
      await axios.put(`${API}/admin/managers/${moduleMgr.id}`, { assigned_modules: moduleIds }, { headers: getHeaders() });
      toast.success('Modules updated');
      setModuleOpen(false);
      load();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed'); }
    finally { setModuleSaving(false); }
  };

  // === PIN Management ===
  const openPin = (mgr) => {
    setPinMgr(mgr);
    setPinValue('');
    setPinOpen(true);
  };

  const handlePinSet = async () => {
    if (pinValue.length !== 4 || !/^\d{4}$/.test(pinValue)) { toast.error('PIN must be exactly 4 digits'); return; }
    setPinSaving(true);
    try {
      await axios.post(`${API}/admin/managers/${pinMgr.id}/pin`, { pin: pinValue }, { headers: getHeaders() });
      toast.success(`PIN set for ${pinMgr.name}`);
      setPinOpen(false);
      load();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed'); }
    finally { setPinSaving(false); }
  };

  const handlePinRemove = async () => {
    setPinSaving(true);
    try {
      await axios.delete(`${API}/admin/managers/${pinMgr.id}/pin`, { headers: getHeaders() });
      toast.success(`PIN removed for ${pinMgr.name}`);
      setPinOpen(false);
      load();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed'); }
    finally { setPinSaving(false); }
  };

  return (
    <div data-testid="admin-managers-page">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 mb-6">
        <div>
          <h2 className="text-lg sm:text-xl tracking-tight font-semibold text-[#1A2421]">User Management</h2>
          <p className="text-sm text-[#5C6B64] mt-1">Manage managers, supervisors, and area managers</p>
        </div>
        <Button onClick={openCreate} className="bg-[color:var(--color-primary)] hover:bg-[color:var(--color-primary-hover)] text-white" data-testid="add-manager-btn">
          <Plus className="w-4 h-4 mr-1.5" /> Add User
        </Button>
      </div>

      {/* Sticky search + lifecycle filter row */}
      <div
        className="sticky top-12 z-10 -mt-2 mb-3 -mx-3 sm:-mx-6 px-3 sm:px-6 py-2 bg-[#FBF8F2]/95 backdrop-blur-sm border-b border-stone-200"
        data-testid="user-search-toolbar"
      >
        <div className="flex flex-col sm:flex-row sm:items-center gap-2">
          {/* Search input */}
          <div className="relative flex-1 min-w-0">
            <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-[#8A9690] pointer-events-none" strokeWidth={1.75} />
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Search by name, email, phone, designation, role…"
              data-testid="user-search-input"
              className="h-9 w-full pl-8 pr-8 rounded-md border border-[#E2E8DF] bg-white text-[13px] focus:outline-none focus:ring-2 focus:ring-[var(--color-primary)]/30 focus:border-[color:var(--color-primary)]/40"
            />
            {searchQuery && (
              <button
                type="button"
                onClick={() => setSearchQuery('')}
                data-testid="user-search-clear"
                className="absolute right-2 top-1/2 -translate-y-1/2 w-5 h-5 rounded-sm text-[#8A9690] hover:bg-stone-100 flex items-center justify-center"
              >
                <X className="w-3 h-3" />
              </button>
            )}
          </div>
          {/* Status pills + count */}
          <div className="flex items-center gap-1 overflow-x-auto" data-testid="user-status-filter">
            {[
              { id: 'active',   label: 'Active' },
              { id: 'inactive', label: 'Inactive' },
              { id: 'deleted',  label: 'Archived' },
              { id: 'all',      label: 'All' },
            ].map(opt => (
              <button key={opt.id} type="button"
                onClick={() => setStatusFilter(opt.id)}
                data-testid={`filter-${opt.id}`}
                className={`shrink-0 text-[12px] h-7 px-3 rounded-full border transition-colors whitespace-nowrap ${
                  statusFilter === opt.id
                    ? 'bg-[color:var(--color-primary)] text-white border-[color:var(--color-primary)] font-semibold'
                    : 'bg-white text-[#5C6B64] border-[#E2E8DF] hover:bg-[#F4F6F2]'
                }`}>
                {opt.label}
              </button>
            ))}
            <span className="ml-2 text-[11px] text-[#8A9690] shrink-0 whitespace-nowrap" data-testid="user-count">
              {(() => {
                const total = managers.length;
                const shown = filteredManagers.length;
                if (!searchQuery) return `${total} ${total === 1 ? 'user' : 'users'}`;
                return `${shown} of ${total} matched`;
              })()}
            </span>
          </div>
        </div>
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-20">
          <div className="animate-spin w-8 h-8 border-2 border-[color:var(--color-primary)] border-t-transparent rounded-full" />
        </div>
      ) : (
        <Card className="border-[#E2E8DF] shadow-sm overflow-hidden">
          <CardContent className="p-0">
            <div className="overflow-x-auto">
              <table className="w-full text-sm text-left text-[#1A2421]">
                <thead>
                  <tr className="text-xs uppercase tracking-[0.05em] text-[#5C6B64] bg-[#F9F9F8] border-b border-[#E2E8DF]">
                    <th className="px-4 py-3 font-medium">Name</th>
                    <th className="px-4 py-3 font-medium">Role</th>
                    <th className="px-4 py-3 font-medium">Assigned Outlets</th>
                    <th className="px-4 py-3 font-medium">Modules</th>
                    <th className="px-4 py-3 font-medium">Status</th>
                    <th className="px-4 py-3 font-medium text-right">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredManagers.length === 0 ? (
                    <tr>
                      <td colSpan={6} className="px-4 py-12 text-center text-[#8A9690]" data-testid="user-empty-state">
                        {searchQuery
                          ? <>No users match <span className="font-semibold text-[#3A1F12]">&ldquo;{searchQuery}&rdquo;</span>.</>
                          : 'No users found in this status filter.'}
                      </td>
                    </tr>
                  ) : filteredManagers.map((m, i) => (
                    <tr key={m.id} className={`border-b border-[#E2E8DF] ${m.status === 'inactive' ? 'opacity-50' : ''} ${i % 2 === 0 ? '' : 'bg-[#F9F9F8]/50'}`}
                      data-testid={`manager-row-${m.id}`}>
                      <td className="px-4 py-3 font-medium">
                        <div>
                          <div className="flex items-center gap-2">
                            <UserCog className="w-4 h-4 text-[#5C6B64] flex-shrink-0" strokeWidth={1.5} />
                            {m.name}
                          </div>
                          {m.designation && (
                            <p className="text-[11px] text-[#1A2421] ml-6 font-medium">{m.designation}</p>
                          )}
                          <p className="text-[10px] text-[#5C6B64] ml-6">{m.email}</p>
                          {m.phone && (
                            <p className="text-[10px] text-[#5C6B64] ml-6 tabular-nums">{m.phone}</p>
                          )}
                        </div>
                      </td>
                      <td className="px-4 py-3">
                        <Badge className={`text-[10px] ${ROLE_COLORS[m.role] || ROLE_COLORS.manager}`} data-testid={`user-role-${m.id}`}>
                          {({'manager': 'Manager', 'supervisor': 'Supervisor', 'area_manager': 'Area Manager', 'production_head': 'Production Head', 'maintenance_manager': 'Maintenance Manager'})[m.role] || m.role}
                        </Badge>
                        {m.pin_set && <Badge className="ml-1 bg-gray-100 text-gray-500 border-gray-200 text-[9px]">PIN</Badge>}
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex flex-wrap gap-1" data-testid={`manager-outlets-${m.id}`}>
                          {(m.assigned_outlets || []).length > 0 ? (
                            m.assigned_outlets.map(o => (
                              <Badge key={o.id} className="bg-[#2D6A4F]/10 text-[#2D6A4F] border-[#2D6A4F]/20 text-[11px]">
                                <Building2 className="w-2.5 h-2.5 mr-0.5" strokeWidth={1.5} />
                                {o.name}
                              </Badge>
                            ))
                          ) : (
                            <Badge className="bg-[#C45A41]/10 text-[#C45A41] border-[#C45A41]/20 text-[11px]">Not Assigned</Badge>
                          )}
                        </div>
                      </td>
                      <td className="px-4 py-3">
                        {(m.role === 'supervisor' || m.role === 'production_head' || m.role === 'maintenance_manager') ? (
                          <div className="flex flex-wrap gap-1" data-testid={`user-modules-${m.id}`}>
                            {(m.assigned_modules || []).length > 0 ? (
                              (m.assigned_modules || []).map(mod => (
                                <Badge key={mod} className="bg-blue-50 text-blue-600 border-blue-200 text-[10px]">
                                  {MODULE_OPTIONS.find(o => o.id === mod)?.label || mod}
                                </Badge>
                              ))
                            ) : (
                              <span className="text-[10px] text-[#C45A41]">None assigned</span>
                            )}
                          </div>
                        ) : (
                          <span className="text-[10px] text-[#5C6B64]">
                            {(m.assigned_modules || []).length > 0
                              ? `${m.assigned_modules.length} modules`
                              : 'All'}
                          </span>
                        )}
                      </td>
                      <td className="px-4 py-3">
                        <Badge className={m.status === 'inactive'
                          ? 'bg-red-50 text-red-600 border-red-200 text-[10px]'
                          : 'bg-emerald-50 text-emerald-600 border-emerald-200 text-[10px]'
                        } data-testid={`manager-status-${m.id}`}>
                          {(m.status || 'active').charAt(0).toUpperCase() + (m.status || 'active').slice(1)}
                        </Badge>
                      </td>
                      <td className="px-4 py-3 text-right">
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <Button variant="ghost" size="sm" className="h-7 w-7 p-0 text-[#5C6B64] hover:text-[#1A2421]"
                              data-testid={`manager-actions-${m.id}`}>
                              <MoreVertical className="w-4 h-4" />
                            </Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end" className="min-w-[160px]">
                            <DropdownMenuItem onClick={() => openEdit(m)} data-testid={`edit-manager-${m.id}`}>
                              <Pencil className="w-3.5 h-3.5 mr-2" /> Edit
                            </DropdownMenuItem>
                            <DropdownMenuItem onClick={() => openAssign(m)}>
                              <Building2 className="w-3.5 h-3.5 mr-2" /> Assign Outlets
                            </DropdownMenuItem>
                            {m.role === 'supervisor' && (
                              <DropdownMenuItem onClick={() => openModules(m)} data-testid={`assign-modules-${m.id}`}>
                                <ClipboardList className="w-3.5 h-3.5 mr-2" /> Assign Modules
                              </DropdownMenuItem>
                            )}
                            <DropdownMenuItem onClick={() => openReset(m)}>
                              <KeyRound className="w-3.5 h-3.5 mr-2" /> Reset Password
                            </DropdownMenuItem>
                            <DropdownMenuItem onClick={() => openPin(m)} data-testid={`set-pin-${m.id}`}>
                              <ShieldCheck className="w-3.5 h-3.5 mr-2" /> {m.pin_set ? 'Reset PIN' : 'Set PIN'}
                            </DropdownMenuItem>
                            {(m.role === 'admin' || m.role === 'area_manager' || m.role === 'super_admin') && (
                              <DropdownMenuItem onClick={() => openSig(m)} data-testid={`manage-signature-${m.id}`}>
                                <PenLine className="w-3.5 h-3.5 mr-2" /> {m.has_signature ? 'Replace Signature' : 'Upload Signature'}
                              </DropdownMenuItem>
                            )}
                            <DropdownMenuSeparator />
                            {m.status === 'deleted' ? (
                              <DropdownMenuItem onClick={() => handleRestore(m)}
                                className="text-emerald-600"
                                data-testid={`restore-manager-${m.id}`}>
                                <Power className="w-3.5 h-3.5 mr-2" /> Restore
                              </DropdownMenuItem>
                            ) : (
                              <>
                                <DropdownMenuItem onClick={() => handleToggleStatus(m)}
                                  className={m.status === 'inactive' ? 'text-emerald-600' : 'text-amber-600'}
                                  data-testid={`toggle-status-${m.id}`}>
                                  <Power className="w-3.5 h-3.5 mr-2" />
                                  {m.status === 'inactive' ? 'Activate' : 'Deactivate'}
                                </DropdownMenuItem>
                                <DropdownMenuItem onClick={() => setDeleteConfirm(m)} className="text-red-600" data-testid={`delete-manager-${m.id}`}>
                                  <Trash2 className="w-3.5 h-3.5 mr-2" /> Archive
                                </DropdownMenuItem>
                              </>
                            )}
                          </DropdownMenuContent>
                        </DropdownMenu>
                      </td>
                    </tr>
                  ))}
                  {managers.length === 0 && (
                    <tr>
                      <td colSpan={6} className="px-4 py-10 text-center text-[#5C6B64]">No users found</td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Assign Outlets Dialog */}
      <Dialog open={assignOpen} onOpenChange={setAssignOpen}>
        <DialogContent className="sm:max-w-md" data-testid="assign-outlets-dialog">
          <DialogHeader>
            <DialogTitle className="text-[#1A2421]">Assign Outlets</DialogTitle>
            <DialogDescription className="text-[#5C6B64]">
              Select outlets for <span className="font-medium text-[#1A2421]">{assignMgr?.name}</span>
            </DialogDescription>
          </DialogHeader>
          <div className="py-3 space-y-2 max-h-60 overflow-y-auto">
            {outlets.map(o => (
              <label key={o.id}
                className={`flex items-center gap-3 px-3 py-2.5 rounded-lg cursor-pointer border transition-colors
                  ${assignIds.includes(o.id) ? 'border-[#2D6A4F]/30 bg-[#2D6A4F]/5' : 'border-[#E2E8DF] hover:bg-[#F9F9F8]'}`}
                data-testid={`assign-checkbox-${o.id}`}>
                <Checkbox checked={assignIds.includes(o.id)} onCheckedChange={() => toggleAssignId(o.id)} />
                <div className="flex items-center gap-2 flex-1 min-w-0">
                  <Building2 className="w-3.5 h-3.5 text-[#5C6B64] flex-shrink-0" />
                  <span className="text-sm text-[#1A2421] truncate">{o.name}</span>
                  {o.city && <span className="text-xs text-[#5C6B64]">({o.city})</span>}
                </div>
              </label>
            ))}
          </div>
          <p className="text-xs text-[#5C6B64]">{assignIds.length} selected</p>
          <DialogFooter>
            <Button variant="outline" onClick={() => setAssignOpen(false)} className="border-[#E2E8DF] text-[#5C6B64]"
              data-testid="assign-cancel-btn">Cancel</Button>
            <Button onClick={handleAssign} disabled={assignSaving || assignIds.length === 0}
              className="bg-[color:var(--color-primary)] hover:bg-[color:var(--color-primary-hover)] text-white" data-testid="assign-save-btn">
              {assignSaving ? 'Saving...' : 'Save'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Create User Dialog */}
      <Dialog open={createOpen} onOpenChange={setCreateOpen}>
        <DialogContent className="sm:max-w-[560px] max-h-[90vh] flex flex-col p-0 gap-0" data-testid="create-manager-dialog">
          <DialogHeader className="px-6 pt-6 pb-3 border-b border-[#F0F2EF] shrink-0">
            <DialogTitle className="text-[#1A2421]">
              {createStep === 'role' ? 'Add User' : `Add ${ROLE_OPTIONS.find(r => r.id === createForm.role)?.label || 'User'}`}
            </DialogTitle>
            <DialogDescription className="text-[#5C6B64]">
              {createStep === 'role'
                ? 'Step 1 of 2 — pick a role. Permissions, module access and operational visibility are derived from this selection.'
                : 'Step 2 of 2 — fill in operational identity and access.'}
            </DialogDescription>
          </DialogHeader>

          {/* ===== Step 1 : Role picker ===== */}
          {createStep === 'role' && (
            <div className="flex-1 overflow-y-auto px-6 py-4">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2" data-testid="role-picker-grid">
                {ROLE_OPTIONS.map(r => (
                  <button key={r.id} type="button"
                    onClick={() => {
                      setCreateForm(p => ({
                        ...p,
                        role: r.id,
                        assigned_modules: ROLE_DEFAULT_MODULES[r.id] || [],
                      }));
                      setCreateStep('details');
                    }}
                    data-testid={`role-card-${r.id}`}
                    className="group text-left p-3.5 rounded-lg border border-[#E2E8DF] bg-white hover:bg-[#F4F6F2] hover:border-[color:var(--color-primary)] transition-colors">
                    <div className="flex items-center justify-between mb-1">
                      <span className="font-semibold text-[13px] text-[#1A2421]">{r.label}</span>
                      <span className={`text-[9px] uppercase tracking-wider px-1.5 py-0.5 rounded ${ROLE_COLORS[r.id]}`}>
                        {r.id.replace('_', ' ')}
                      </span>
                    </div>
                    <p className="text-[11px] text-[#5C6B64] leading-snug">{r.desc}</p>
                  </button>
                ))}
              </div>
              <p className="text-[10px] text-[#8A9690] mt-4">
                Super Admins are managed separately via platform governance and never appear in this list.
              </p>
            </div>
          )}

          {/* ===== Step 2 : Profile form ===== */}
          {createStep === 'details' && (
          <div className="flex-1 overflow-y-auto px-6 py-4 space-y-4">
            <div className="flex items-center gap-2 -mt-1 mb-1">
              <button type="button" onClick={() => setCreateStep('role')}
                data-testid="back-to-role-step"
                className="text-[11px] text-[#5C6B64] hover:text-[color:var(--color-primary)] flex items-center gap-1">
                ← Change role
              </button>
              <span className={`text-[10px] uppercase tracking-wider px-1.5 py-0.5 rounded border ${ROLE_COLORS[createForm.role] || ''}`}>
                {ROLE_OPTIONS.find(r => r.id === createForm.role)?.label || createForm.role}
              </span>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5">
                <Label className="text-xs text-[#5C6B64]">Name <span className="text-[#C45A41]">*</span></Label>
                <Input data-testid="create-name" value={createForm.name}
                  onChange={e => setCreateForm(p => ({ ...p, name: e.target.value }))}
                  placeholder="Full name" className="h-9 border-[#E2E8DF]" />
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs text-[#5C6B64]">Email <span className="text-[#C45A41]">*</span></Label>
                <Input data-testid="create-email" type="email" value={createForm.email}
                  onChange={e => setCreateForm(p => ({ ...p, email: e.target.value }))}
                  placeholder="user@outlet.com" className="h-9 border-[#E2E8DF]" />
              </div>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div className="space-y-1.5">
                <Label className="text-xs text-[#5C6B64]">Designation / Job title</Label>
                <Input data-testid="create-designation" value={createForm.designation}
                  onChange={e => setCreateForm(p => ({ ...p, designation: e.target.value }))}
                  placeholder="e.g. Head of F&B Operations" className="h-9 border-[#E2E8DF]" />
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs text-[#5C6B64]">Mobile number</Label>
                <Input data-testid="create-phone" value={createForm.phone}
                  onChange={e => setCreateForm(p => ({ ...p, phone: e.target.value }))}
                  placeholder="+91 98xxxxxxxx" className="h-9 border-[#E2E8DF]" />
              </div>
            </div>
            <p className="text-[10px] text-[#5C6B64] -mt-2">Designation appears on vouchers, signatures and exported documents.</p>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5">
                <Label className="text-xs text-[#5C6B64]">Password <span className="text-[#C45A41]">*</span></Label>
                <div className="relative">
                  <Input data-testid="create-password" type={showPassword ? 'text' : 'password'} value={createForm.password}
                    onChange={e => setCreateForm(p => ({ ...p, password: e.target.value }))}
                    placeholder="Min 6 chars" className="h-9 border-[#E2E8DF] pr-8" />
                  <button type="button" onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-2 top-1/2 -translate-y-1/2 text-[#5C6B64]">
                    {showPassword ? <EyeOff className="w-3.5 h-3.5" /> : <Eye className="w-3.5 h-3.5" />}
                  </button>
                </div>
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs text-[#5C6B64]">PIN (optional, 4 digits)</Label>
                <Input data-testid="create-pin" type="text" maxLength={4} value={createForm.pin}
                  onChange={e => setCreateForm(p => ({ ...p, pin: e.target.value.replace(/\D/g, '').slice(0, 4) }))}
                  placeholder="1234" className="h-9 border-[#E2E8DF]" />
              </div>
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs text-[#5C6B64]">Assign Outlets <span className="text-[#C45A41]">*</span></Label>
              <div className="space-y-1 max-h-32 overflow-y-auto border border-[#E2E8DF] rounded-lg p-2">
                {outlets.map(o => (
                  <label key={o.id}
                    className={`flex items-center gap-2 px-2 py-1.5 rounded cursor-pointer transition-colors
                      ${createForm.outlet_ids.includes(o.id) ? 'bg-[#2D6A4F]/5' : 'hover:bg-[#F9F9F8]'}`}
                    data-testid={`create-outlet-${o.id}`}>
                    <Checkbox checked={createForm.outlet_ids.includes(o.id)} onCheckedChange={() => toggleCreateOutlet(o.id)} />
                    <span className="text-xs text-[#1A2421]">{o.name}</span>
                  </label>
                ))}
              </div>
              <p className="text-[10px] text-[#5C6B64]">{createForm.outlet_ids.length} selected</p>
            </div>
            {createForm.role !== 'area_manager' && (
              <div className="space-y-1.5">
                <Label className="text-xs text-[#5C6B64]">
                  Module Access
                  {createForm.role === 'supervisor' && <span className="text-[#C45A41]"> *</span>}
                </Label>
                <p className="text-[10px] text-[#5C6B64]">
                  {createForm.role === 'supervisor'
                    ? 'Supervisor sees & edits only the modules assigned here. Nothing else is visible in the sidebar.'
                    : createForm.role === 'production_head'
                      ? 'Pick the modules this user should see. Admin-only modules (user management, outlets) are never granted via checklist.'
                      : 'Leave empty for full access. Pick modules to restrict access to specific areas.'}
                </p>
                <div className="flex flex-wrap gap-1.5" data-testid="create-modules">
                  {MODULE_OPTIONS.map(mod => (
                    <button key={mod.id} type="button"
                      onClick={() => setCreateForm(p => ({
                        ...p, assigned_modules: p.assigned_modules.includes(mod.id)
                          ? p.assigned_modules.filter(x => x !== mod.id) : [...p.assigned_modules, mod.id]
                      }))}
                      className={`px-2.5 py-1 rounded text-xs border transition-colors ${createForm.assigned_modules.includes(mod.id)
                        ? 'border-blue-400 bg-blue-50 text-blue-700 font-medium'
                        : 'border-[#E2E8DF] text-[#5C6B64] hover:bg-[#F9F9F8]'}`}
                      data-testid={`create-module-${mod.id}`}>
                      {mod.label}
                    </button>
                  ))}
                </div>
                <p className="text-[10px] text-[#5C6B64]">{createForm.assigned_modules.length} selected</p>
              </div>
            )}
          </div>
          )}
          <DialogFooter className="px-6 py-3 border-t border-[#F0F2EF] shrink-0">
            {createStep === 'role' ? (
              <Button variant="outline" onClick={() => setCreateOpen(false)} className="border-[#E2E8DF] text-[#5C6B64]">Cancel</Button>
            ) : (
              <>
                <Button variant="outline" onClick={() => setCreateOpen(false)} className="border-[#E2E8DF] text-[#5C6B64]">Cancel</Button>
                <Button onClick={handleCreate} disabled={creating}
                  className="bg-[color:var(--color-primary)] hover:bg-[color:var(--color-primary-hover)] text-white" data-testid="create-submit">
                  {creating ? 'Creating...' : `Create ${ROLE_OPTIONS.find(r => r.id === createForm.role)?.label || 'User'}`}
                </Button>
              </>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Reset Password Dialog */}
      <Dialog open={resetOpen} onOpenChange={setResetOpen}>
        <DialogContent className="sm:max-w-sm" data-testid="reset-password-dialog">
          <DialogHeader>
            <DialogTitle className="text-[#1A2421]">Reset Password</DialogTitle>
            <DialogDescription className="text-[#5C6B64]">
              Set a new password for <span className="font-medium text-[#1A2421]">{resetMgr?.name}</span>
            </DialogDescription>
          </DialogHeader>
          <div className="py-3">
            <Label className="text-sm text-[#5C6B64]">New Password <span className="text-[#C45A41]">*</span></Label>
            <div className="relative mt-1.5">
              <Input data-testid="new-password-input" type={showNewPw ? 'text' : 'password'} value={newPassword}
                onChange={e => setNewPassword(e.target.value)} placeholder="Min 6 characters"
                className="h-11 bg-[#F9F9F8] border-[#E2E8DF] focus:border-[color:var(--color-primary)] focus:ring-1 focus:ring-[var(--color-primary)] pr-10" />
              <button type="button" onClick={() => setShowNewPw(!showNewPw)}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-[#5C6B64] hover:text-[color:var(--color-primary)]"
                data-testid="toggle-new-pw-visibility">
                {showNewPw ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
              </button>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setResetOpen(false)} className="border-[#E2E8DF] text-[#5C6B64]"
              data-testid="reset-cancel-btn">Cancel</Button>
            <Button onClick={handleReset} disabled={resetting || newPassword.length < 6}
              className="bg-[#D4A017] hover:bg-[#B88B14] text-white" data-testid="reset-save-btn">
              {resetting ? 'Resetting...' : 'Reset Password'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit User Dialog */}
      <Dialog open={editOpen} onOpenChange={setEditOpen}>
        <DialogContent className="sm:max-w-[480px] max-h-[90vh] flex flex-col p-0 gap-0" data-testid="edit-manager-dialog">
          <DialogHeader className="px-6 pt-6 pb-3 border-b border-[#F0F2EF] shrink-0">
            <DialogTitle className="text-[#1A2421]">Edit User</DialogTitle>
            <DialogDescription className="text-[#5C6B64]">
              Update <span className="font-medium text-[#1A2421]">{editMgr?.name}</span>
            </DialogDescription>
          </DialogHeader>
          <div className="flex-1 overflow-y-auto px-6 py-4 space-y-3">
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5">
                <Label className="text-xs text-[#5C6B64]">Name</Label>
                <Input value={editForm.name} onChange={e => setEditForm(f => ({ ...f, name: e.target.value }))}
                  className="h-9 border-[#E2E8DF]" data-testid="edit-mgr-name" />
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs text-[#5C6B64]">Email</Label>
                <Input value={editForm.email} onChange={e => setEditForm(f => ({ ...f, email: e.target.value }))}
                  className="h-9 border-[#E2E8DF]" data-testid="edit-mgr-email" />
              </div>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div className="space-y-1.5">
                <Label className="text-xs text-[#5C6B64]">Designation / Job title</Label>
                <Input value={editForm.designation}
                  onChange={e => setEditForm(f => ({ ...f, designation: e.target.value }))}
                  placeholder="e.g. Head of F&B Operations"
                  className="h-9 border-[#E2E8DF]" data-testid="edit-mgr-designation" />
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs text-[#5C6B64]">Mobile number</Label>
                <Input value={editForm.phone}
                  onChange={e => setEditForm(f => ({ ...f, phone: e.target.value }))}
                  placeholder="+91 98xxxxxxxx"
                  className="h-9 border-[#E2E8DF]" data-testid="edit-mgr-phone" />
              </div>
            </div>
            <p className="text-[10px] text-[#5C6B64] -mt-2">Designation appears on vouchers, signatures and exported documents.</p>
            <div className="space-y-1.5">
              <Label className="text-xs text-[#5C6B64]">Role</Label>
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                {ROLE_OPTIONS.map(r => (
                  <button key={r.id} type="button" onClick={() => setEditForm(f => ({ ...f, role: r.id }))}
                    className={`px-2 py-1.5 rounded-lg border text-xs text-center transition-colors ${editForm.role === r.id
                      ? 'border-[color:var(--color-primary)] bg-[color:var(--color-primary)]/5 text-[color:var(--color-primary)] font-medium'
                      : 'border-[#E2E8DF] text-[#5C6B64] hover:bg-[#F9F9F8]'}`}
                    data-testid={`edit-role-${r.id}`}>
                    {r.label}
                  </button>
                ))}
              </div>
            </div>
            {editForm.role !== 'area_manager' && (
              <div className="space-y-1.5">
                <Label className="text-xs text-[#5C6B64]">Module Access</Label>
                <p className="text-[10px] text-[#5C6B64]">
                  {editForm.role === 'supervisor'
                    ? 'Supervisor sees & edits only these modules. Everything else stays hidden.'
                    : editForm.role === 'production_head'
                      ? 'Modules control every screen this user can open.'
                      : 'Leave empty for full access. Pick modules to restrict.'}
                </p>
                <div className="flex flex-wrap gap-1.5" data-testid="edit-modules">
                {MODULE_OPTIONS.map(mod => {
                  const checked = (editForm.assigned_modules || []).includes(mod.id);
                  return (
                    <button key={mod.id} type="button"
                      onClick={() => setEditForm(f => ({
                        ...f, assigned_modules: (f.assigned_modules || []).includes(mod.id)
                          ? (f.assigned_modules || []).filter(x => x !== mod.id)
                          : [...(f.assigned_modules || []), mod.id]
                      }))}
                      className={`px-2 py-1 rounded text-[10px] border transition-colors ${checked
                        ? 'border-blue-400 bg-blue-50 text-blue-700 font-medium'
                        : 'border-[#E2E8DF] text-[#5C6B64]'}`}
                      data-testid={`edit-module-${mod.id}`}>
                      {mod.label}
                    </button>
                  );
                })}
                </div>
              </div>
            )}
          </div>
          <DialogFooter className="px-6 py-3 border-t border-[#F0F2EF] shrink-0">
            <Button variant="outline" onClick={() => setEditOpen(false)} className="border-[#E2E8DF] text-[#5C6B64]">Cancel</Button>
            <Button onClick={handleEdit} disabled={editSaving} className="bg-[color:var(--color-primary)] hover:bg-[color:var(--color-primary-hover)] text-white" data-testid="edit-mgr-save">
              {editSaving ? 'Saving...' : 'Save'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Module Assignment Dialog */}
      <Dialog open={moduleOpen} onOpenChange={setModuleOpen}>
        <DialogContent className="sm:max-w-sm" data-testid="module-assignment-dialog">
          <DialogHeader>
            <DialogTitle className="text-[#1A2421] flex items-center gap-2">
              <ClipboardList className="w-5 h-5 text-blue-600" /> Assign Modules
            </DialogTitle>
            <DialogDescription className="text-[#5C6B64]">
              Select modules for <span className="font-medium text-[#1A2421]">{moduleMgr?.name}</span> to edit
            </DialogDescription>
          </DialogHeader>
          <div className="py-3 space-y-2">
            {MODULE_OPTIONS.map(mod => (
              <label key={mod.id}
                className={`flex items-center gap-3 px-3 py-2.5 rounded-lg cursor-pointer border transition-colors
                  ${moduleIds.includes(mod.id) ? 'border-blue-300 bg-blue-50' : 'border-[#E2E8DF] hover:bg-[#F9F9F8]'}`}>
                <Checkbox checked={moduleIds.includes(mod.id)}
                  onCheckedChange={() => setModuleIds(p => p.includes(mod.id) ? p.filter(x => x !== mod.id) : [...p, mod.id])} />
                <span className="text-sm text-[#1A2421]">{mod.label}</span>
              </label>
            ))}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setModuleOpen(false)} className="border-[#E2E8DF] text-[#5C6B64]">Cancel</Button>
            <Button onClick={handleModuleSave} disabled={moduleSaving}
              className="bg-blue-600 hover:bg-blue-700 text-white" data-testid="module-save-btn">
              {moduleSaving ? 'Saving...' : `Save (${moduleIds.length} selected)`}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* PIN Management Dialog */}
      <Dialog open={pinOpen} onOpenChange={setPinOpen}>
        <DialogContent className="sm:max-w-xs" data-testid="pin-dialog">
          <DialogHeader>
            <DialogTitle className="text-[#1A2421] flex items-center gap-2">
              <ShieldCheck className="w-5 h-5 text-[color:var(--color-primary)]" /> {pinMgr?.pin_set ? 'Reset' : 'Set'} PIN
            </DialogTitle>
            <DialogDescription className="text-[#5C6B64]">
              {pinMgr?.pin_set ? 'Change or remove' : 'Set a 4-digit'} PIN for <span className="font-medium text-[#1A2421]">{pinMgr?.name}</span>
            </DialogDescription>
          </DialogHeader>
          <div className="py-3">
            <Label className="text-xs text-[#5C6B64]">4-Digit PIN</Label>
            <Input type="text" maxLength={4} value={pinValue}
              onChange={e => setPinValue(e.target.value.replace(/\D/g, '').slice(0, 4))}
              placeholder="1234" className="h-10 border-[#E2E8DF] mt-1 text-center text-lg tracking-[0.5em] font-mono"
              data-testid="pin-input" />
          </div>
          <DialogFooter className="flex gap-2">
            {pinMgr?.pin_set && (
              <Button variant="outline" onClick={handlePinRemove} disabled={pinSaving}
                className="border-red-200 text-red-600 hover:bg-red-50" data-testid="pin-remove-btn">
                Remove PIN
              </Button>
            )}
            <Button onClick={handlePinSet} disabled={pinSaving || pinValue.length !== 4}
              className="bg-[color:var(--color-primary)] hover:bg-[color:var(--color-primary-hover)] text-white" data-testid="pin-save-btn">
              {pinSaving ? '...' : 'Set PIN'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation */}
      <AlertDialog open={!!deleteConfirm} onOpenChange={() => setDeleteConfirm(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Archive {deleteConfirm?.name}?</AlertDialogTitle>
            <AlertDialogDescription>
              This user will lose access immediately and move to the Archived tab. All
              historical references — vouchers, approvals, audit logs — are preserved
              with their identity at the time of action. You can Restore them anytime.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleDelete} className="bg-red-600 hover:bg-red-700 text-white" data-testid="confirm-delete-manager">
              Archive
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Signature management — approver users only */}
      <Dialog open={sigOpen} onOpenChange={setSigOpen}>
        <DialogContent className="sm:max-w-sm" data-testid="signature-dialog">
          <DialogHeader>
            <DialogTitle className="text-[#1A2421]">Manage Signature</DialogTitle>
            <DialogDescription className="text-[#5C6B64]">
              {sigMgr?.name} · {sigMgr?.role}. Used on petty cash vouchers approved by this user.
            </DialogDescription>
          </DialogHeader>
          <div className="flex items-center gap-3">
            {sigMgr?.signature_b64 ? (
              <img src={sigMgr.signature_b64} alt="signature"
                className="h-14 max-w-[200px] object-contain border border-[#E2E8DF] rounded bg-white p-1"
                data-testid="signature-current-preview" />
            ) : (
              <div className="h-14 w-[200px] border border-dashed border-[#E2E8DF] rounded text-[10px] text-[#9CA3AF] flex items-center justify-center">
                No signature uploaded
              </div>
            )}
          </div>
          <Label className="text-xs text-[#5C6B64]">Recommended: PNG / JPG with a transparent or white background, under 1.2 MB.</Label>
          <input id="sig-file" type="file" accept="image/*" onChange={handleSignatureUpload}
            className="text-xs file:mr-3 file:rounded file:border-0 file:bg-[color:var(--color-primary)] file:text-white file:px-3 file:py-1.5 file:cursor-pointer hover:file:bg-[var(--color-primary-hover)]"
            data-testid="signature-file-input" />
          <DialogFooter>
            <Button variant="outline" onClick={() => setSigOpen(false)}
              className="border-[#E2E8DF] text-[#5C6B64]" disabled={sigSaving} data-testid="signature-close">
              Close
            </Button>
            {sigSaving && <span className="text-xs text-[#5C6B64] flex items-center gap-1"><Loader2 className="w-3 h-3 animate-spin" /> Uploading…</span>}
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
